import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileBadge, ChevronRight, ChevronLeft, Loader2, CheckCircle2, AlertTriangle, Upload, Palette, Database, Wand2 } from "lucide-react";

const AVAIL = [
  ["tech_profile", "Technology Profile"], ["reconciliation", "Reconciliation"], ["implementation_report", "Implementation Report"],
  ["evidence", "Accepted Evidence"], ["policies", "Approved Policies"], ["existing_ssps", "Existing SSP"],
];

export default function SSPBuilder() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(sp.get("customer") || "");
  const [ctx, setCtx] = useState(null);
  const [fromSsp, setFromSsp] = useState("");
  const [branding, setBranding] = useState({ primary: "1F4E79", secondary: "C55A11" });
  const [logoName, setLogoName] = useState("");
  const [intake, setIntake] = useState(null);
  const [edits, setEdits] = useState({});
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const logoRef = useRef();

  useEffect(() => { api.get("/ssp/customers").then((r) => setCustomers(r.data.customers || [])).catch(() => {}); }, []);

  const loadCtx = useCallback(() => {
    if (!customerId) return;
    api.get(`/ssp/builder/${customerId}/context`).then((r) => setCtx(r.data)).catch(() => {});
    api.get(`/ssp/builder/${customerId}/intake`).then((r) => { setIntake(r.data); setBranding((b) => ({ ...b, ...(r.data.branding || {}) })); setLogoName(r.data.branding?.logo_name || ""); }).catch(() => {});
  }, [customerId]);
  useEffect(() => { loadCtx(); }, [loadCtx]);

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post(`/ssp/builder/${customerId}/branding/logo`, fd); setLogoName(r.data.logo_name); toast.success("Logo uploaded."); }
    catch (er) { toast.error(er.response?.data?.detail || "Logo upload failed"); }
  };

  const saveBranding = async () => {
    await api.put(`/ssp/builder/${customerId}/intake`, { fields: {}, branding });
    toast.success("Branding saved.");
    setStep(4);
  };

  const saveIntake = async () => {
    if (Object.keys(edits).length) {
      await api.put(`/ssp/builder/${customerId}/intake`, { fields: edits });
      const r = await api.get(`/ssp/builder/${customerId}/intake`); setIntake(r.data); setEdits({});
      toast.success("Information saved.");
    }
    return true;
  };

  const generate = async () => {
    setBusy(true);
    try {
      await saveIntake();
      const r = await api.post(`/ssp/builder/${customerId}/generate`, { from_ssp_id: fromSsp || null });
      setJob({ id: r.data.job_id, ssp_id: r.data.ssp_id, status: "Queued", progress: 0 });
      setStep(5);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? (d.message || "Generation failed to start") : (d || "Generation failed to start"));
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!job?.id || ["Awaiting Statement Review", "Failed"].includes(job.status)) return;
    const iv = setInterval(async () => {
      try { const r = await api.get(`/ssp/jobs/${job.id}`); setJob((j) => ({ ...j, ...r.data })); if (["Awaiting Statement Review", "Failed"].includes(r.data.status)) clearInterval(iv); } catch {}
    }, 2500);
    return () => clearInterval(iv);
  }, [job?.id, job?.status]);

  const info = intake?.information_needed || [];
  const grouped = intake?.grouped || {};
  const REQUIRED = ["SYSTEM_NAME", "SYSTEM_OWNER", "USERS_IN_SCOPE", "CUI_INFORMATION_TYPES", "CUI_STORAGE_TRANSMISSION"];
  const curVal = (k) => (k in edits ? edits[k] : (intake?.fields?.[k]?.value || ""));
  const stillMissingRequired = REQUIRED.filter((k) => !String(curVal(k)).trim());
  const contrast = (hex) => {
    const h = (hex || "").replace("#", "");
    if (h.length !== 6) return "#FFFFFF";
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
    return (0.299 * r + 0.587 * g + 0.114 * b) < 150 ? "#FFFFFF" : "#0B1F3A";
  };

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2"><FileBadge className="h-6 w-6 text-gold" /> New SSP</h1>
      </header>

      <div className="p-6 md:p-8">
        <div className="flex items-center gap-2 mb-6 text-xs">
          {["Customer", "Data", "Branding", "Information", "Generate"].map((s, i) => (
            <div key={s} className={`flex items-center gap-2 ${step === i + 1 ? "text-navy font-semibold" : "text-slate-400"}`}>
              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] ${step >= i + 1 ? "bg-gold text-navy" : "bg-slate-200 text-slate-500"}`}>{i + 1}</span>{s}
              {i < 4 && <ChevronRight className="h-4 w-4" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="max-w-md space-y-4" data-testid="ssp-step-customer">
            <Label className="text-xs">Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="ssp-customer"><SelectValue placeholder="Select a customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="bg-navy text-white hover:bg-navy-700" disabled={!customerId} onClick={() => setStep(2)} data-testid="ssp-next-1">Next <ChevronRight className="h-4 w-4 ml-1" /></Button>
          </div>
        )}

        {step === 2 && ctx && (
          <div className="space-y-4 max-w-2xl" data-testid="ssp-step-data">
            <div className="rounded border border-border p-4 bg-card">
              <div className="font-semibold text-navy mb-3 flex items-center gap-2"><Database className="h-4 w-4 text-gold-dark" /> Available data for {ctx.customer_name}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                {AVAIL.map(([k, label]) => {
                  const v = ctx[k]; const ok = Array.isArray(v) ? v.length > 0 : !!v;
                  const count = Array.isArray(v) ? v.length : (typeof v === "number" ? v : "");
                  return <div key={k} className="flex items-center gap-1.5">{ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-slate-300" />}<span className={ok ? "text-navy" : "text-slate-400"}>{label}{count !== "" && ok ? ` (${count})` : ""}</span></div>;
                })}
                <div className="flex items-center gap-1.5"><span className="text-muted-foreground">Partner:</span> <b className="text-navy">{ctx.implementation_partner || "—"}</b></div>
              </div>
            </div>
            {ctx.existing_ssps?.some((s) => s.status === "Approved") && (
              <div className="rounded border border-border p-3 space-y-2">
                <Label className="text-xs">Create a new version from an approved SSP (optional)</Label>
                <Select value={fromSsp || "none"} onValueChange={(v) => setFromSsp(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="ssp-from"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">New SSP</SelectItem>{ctx.existing_ssps.filter((s) => s.status === "Approved").map((s) => <SelectItem key={s.id} value={s.id}>v{s.version} ({s.status})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2"><Button variant="outline" onClick={() => setStep(1)}><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
              <Button className="bg-navy text-white hover:bg-navy-700" onClick={() => setStep(3)} data-testid="ssp-next-2">Next <ChevronRight className="h-4 w-4 ml-1" /></Button></div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 max-w-2xl" data-testid="ssp-step-branding">
            <div className="rounded border border-border p-4 bg-card space-y-4">
              <div className="font-semibold text-navy flex items-center gap-2"><Palette className="h-4 w-4 text-gold-dark" /> Client co-branding</div>
              <p className="text-xs text-muted-foreground">Controlled co-branding only. The ComplyX Gov identity and "Prepared by ComplyX Gov" are always retained. Customer colors apply only to cover accents, table headers, and section accents — never to body text or status colors.</p>
              <div>
                <Label className="text-xs">Client logo (PNG/JPG)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input ref={logoRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={uploadLogo} data-testid="ssp-logo-input" />
                  <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} data-testid="ssp-logo-upload"><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload logo</Button>
                  {logoName && <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {logoName}</span>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {["primary", "secondary"].map((k) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs capitalize">{k} color</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={`#${branding[k] || "000000"}`} onChange={(e) => setBranding((b) => ({ ...b, [k]: e.target.value.replace("#", "").toUpperCase() }))} className="h-8 w-10 rounded border border-border" data-testid={`ssp-color-${k}`} />
                      <Input value={branding[k] || ""} onChange={(e) => setBranding((b) => ({ ...b, [k]: e.target.value.replace("#", "").toUpperCase() }))} className="h-8 text-xs font-mono w-28" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded border border-border overflow-hidden" data-testid="ssp-branding-preview">
                <div className="px-4 py-3 text-sm font-semibold" style={{ backgroundColor: `#${branding.primary}`, color: contrast(branding.primary) }}>Cover / Section Accent Preview</div>
                <div className="px-4 py-2 text-xs" style={{ backgroundColor: `#${branding.secondary}`, color: contrast(branding.secondary) }}>Table header accent · Prepared by ComplyX Gov</div>
              </div>
            </div>
            <div className="flex gap-2"><Button variant="outline" onClick={() => setStep(2)}><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
              <Button className="bg-navy text-white hover:bg-navy-700" onClick={saveBranding} data-testid="ssp-next-3">Save &amp; Continue <ChevronRight className="h-4 w-4 ml-1" /></Button></div>
          </div>
        )}

        {step === 4 && intake && (
          <div className="space-y-4" data-testid="ssp-step-information">
            {info.length > 0 && <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {info.length} item(s) are missing. Required items must be completed to generate. Everything else is pre-filled from existing Command Center data.</div>}
            {Object.entries(grouped).map(([g, fields]) => (
              <div key={g} className="rounded border border-border">
                <div className="px-3 py-2 bg-slate-50 border-b text-xs font-semibold text-navy">{g}</div>
                <div className="divide-y divide-border/60">
                  {fields.map((f) => (
                    <div key={f.field_key} className="px-3 py-2.5 grid sm:grid-cols-[1fr_1.6fr_auto] gap-2 items-center text-sm" data-testid={`ssp-field-${f.field_key}`}>
                      <div><div className="font-medium text-navy text-xs">{f.label}{REQUIRED.includes(f.field_key) && <span className="text-red-600">*</span>}</div><div className="text-[10px] text-muted-foreground">{f.source || "—"}</div></div>
                      {f.options?.length ? (
                        <Select value={edits[f.field_key] ?? f.value} onValueChange={(v) => setEdits((e) => ({ ...e, [f.field_key]: v }))}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`ssp-val-${f.field_key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                          <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <Input value={edits[f.field_key] ?? f.value} onChange={(e) => setEdits((s) => ({ ...s, [f.field_key]: e.target.value }))} className="h-8 text-xs" data-testid={`ssp-val-${f.field_key}`} />
                      )}
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${String(curVal(f.field_key)).trim() ? "bg-emerald-100 text-emerald-800" : "bg-red-50 text-red-700"}`}>{String(curVal(f.field_key)).trim() ? "Provided" : "Missing"}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(3)}><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
              <Button variant="outline" onClick={saveIntake} data-testid="ssp-save-info"><CheckCircle2 className="h-4 w-4 mr-1" /> Save</Button>
              <Button className="bg-gold text-navy hover:bg-gold-dark" disabled={busy || stillMissingRequired.length > 0} onClick={generate} data-testid="ssp-generate">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5" />} Generate Draft Statements
              </Button>
            </div>
            {stillMissingRequired.length > 0 && <p className="text-xs text-red-600">Provide required fields (marked *) before generating: {stillMissingRequired.join(", ")}</p>}
          </div>
        )}

        {step === 5 && job && (
          <div className="max-w-xl space-y-4" data-testid="ssp-step-generate">
            {job.status !== "Awaiting Statement Review" && job.status !== "Failed" && (
              <div className="rounded border border-border p-5 space-y-2">
                <div className="flex items-center gap-2 text-navy text-sm"><Loader2 className="h-4 w-4 animate-spin text-gold-dark" /> {job.stage || "Queued"} — drafting implementation statements for all 110 controls. You may leave and return.</div>
                <div className="h-2 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-gold transition-all duration-500" style={{ width: `${job.progress || 5}%` }} /></div>
              </div>
            )}
            {job.status === "Awaiting Statement Review" && (
              <div className="rounded border border-emerald-200 bg-emerald-50 p-5 space-y-3" data-testid="ssp-gen-complete">
                <div className="flex items-center gap-2 text-emerald-800 font-medium"><CheckCircle2 className="h-5 w-5" /> Draft statements ready for all 110 controls.</div>
                <p className="text-xs text-emerald-900">Review and approve statements, then generate the ComplyX-branded DOCX.</p>
                <Button className="bg-navy text-white hover:bg-navy-700" onClick={() => nav(`/operations/ssp/${job.ssp_id}/controls`)} data-testid="ssp-goto-controls">Review Controls <ChevronRight className="h-4 w-4 ml-1" /></Button>
              </div>
            )}
            {job.status === "Failed" && <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">Generation failed: {job.error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
